/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { FormMode } from 'app/shared/enumeration/form-mode';
import { ForgotPasswordModel } from '../models/forgot-password-model';
import { ForgotPasswordService } from '../services/forgot-password-service';

@Component({
	selector: 'forgot-password-form',
	templateUrl: './forgot-password-form.component.html',
	styleUrls: ['./forgot-password-form.component.scss'],
	standalone: false,
})
export class ForgotPasswordFormComponent extends CreateUpdateBaseComponent<ForgotPasswordModel> implements OnInit {
	// shortcuts for validation
	get email(): AbstractControl {
		return this.dataForm.controls['email'];
	}

	constructor(
		protected fb: FormBuilder,
		private service: ForgotPasswordService,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
		this.data = { itemId: 0, formMode: FormMode.Create };
	}

	protected createFormGroup(): FormGroup<any> {
		return this.fb.group(
			{
				email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(RegexValidationStrings.EMAIL)]],
			},
			{},
		);
	}

	protected populateControls(datafrom: FormGroup<any>) {}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<ForgotPasswordModel> {
		return Promise.resolve(new ForgotPasswordModel());
	}

	protected createUpdateModel() {
		return {
			email: this.email.value,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		throw 'Not supported.';
	}

	protected async doCreate(dataToUpdate: any): Promise<any> {
		return await this.service.submitForgotPasswordRequest(dataToUpdate);
	}

	onForgotPasswordSubmit() {
		this.save();
	}

	// overidden from CreateUpdateBaseComponent base class
	// as this is an inline form, not an aside
	save() {
		if (this.formHelperService.isFormValid(this.dataForm)) {
			const updateModel = this.createUpdateModel();

			this.blockUI.start();
			this.doCreate(updateModel)
				.then((response: any) => {
					this.closeForm();
				})
				.finally(() => {
					this.blockUI.stop();
				});
		}
	}

	// overidden from CreateUpdateBaseComponent base class
	cancel() {
		// No confirmation requested.
		this.closeForm();
	}

	onCancel(event) {
		event.preventDefault();
		this.cancel();
	}

	closeForm() {
		// Rather than raising events, it may make more sense to reload the sign-in page, which will ensure it's in a clean state.
		location.reload();
	}
}
